/**
 * Awan LRLC, diwarnai menurut kelasnya — dan sumbunya milik yang membacanya.
 *
 * Gambar ini dahulu satu crossplot yang tetap: RT mendatar, PHIE tegak. Itu
 * memang pasangan yang paling sering benar, dan ia tetap yang terpasang
 * sebelum siapa pun memilih — sebuah lapisan berpori yang resistivitasnya
 * rendah adalah persis yang sedang dicari layar ini, dan di gambar itulah ia
 * berdiri terpisah dari serpih rapat yang resistivitasnya juga rendah.
 *
 * Tetapi penggolongan ini dibantah pada sumbu yang lain. RWA terhadap VSH
 * memperlihatkan apakah "air" yang dibaca log memang air atau lempung yang
 * menghantar; NPHI terhadap RHOB tidak membaca resistivitas sama sekali, dan
 * karena itu satu-satunya yang bisa membantahnya tanpa memakai angka yang
 * sama. Kesepuluh besaran dikirim bersama tiap sampel — lihat `LRLC_AXES` di
 * server — jadi menggeser sumbu tidak menuntut satu pun perjalanan baru, dan
 * yang menggesernya menggesernya lima kali berturut-turut.
 *
 * **Zum bekerja pada jendela nilainya, bukan pada gambarnya.** Yang berubah
 * adalah dari berapa sampai berapa sumbunya membentang, dan angka di tepinya
 * dihitung ulang dari jendela itu: sumbu resistivitas yang diperbesar dari
 * 0,1–1000 menjadi 2–20 memulangkan tanda 2 · 5 · 10 · 20. Sebuah gambar yang
 * diperbesar sambil menyisakan tanda sumbu yang lama adalah awan berwarna,
 * bukan pengukuran.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Empty } from "./ui";
import MapZoomControls from "./MapZoomControls";
import { colorOf, occupied } from "../lib/lrlc";
import {
  DEFAULT_X,
  DEFAULT_Y,
  LRLC_CHANNELS,
  captionOf,
  channelOf,
} from "../lib/lrlcplot";
import {
  WHOLE,
  type Window,
  isWhole,
  panWindow,
  placeOn,
  scaleOf,
  ticksOn,
  windowScale,
  zoomOf,
  zoomWindow,
} from "../lib/plotzoom";
import type { LrlcPayload } from "../types";

/** Bingkai gambar, sama dengan crossplot Rw supaya keduanya sepadan. */
const PAD = 3;
const SPAN = 100 - PAD * 2;

/** Seberapa jauh satu putaran roda memperbesar. */
const WHEEL = 1.25;
/** Seberapa jauh satu tekan tombol memperbesar — lebih halus daripada roda. */
const BUTTON = 1.4;

/**
 * Kedudukan dalam kotak nilainya, 0..1, dari piksel di dalam elemennya.
 *
 * Bingkai `PAD` ikut dihitung, dan itu bukan kerewelan: tanpanya setiap zum
 * meleset tiga persen lebar kotak dari tempat kursornya berada, yang pada
 * jendela selebar sepersepuluh sumbu berarti sepertiga jendela.
 */
function within(pixels: number, size: number): number {
  if (!(size > 0)) return 0.5;
  return Math.min(1, Math.max(0, ((pixels / size) * 100 - PAD) / SPAN));
}

/** Pecahan sumbu **penuh** yang sedang ditunjuk kursor. */
function pointing(window: Window, t: number): number {
  return window.lo + t * (window.hi - window.lo);
}

export default function LrlcCrossplot({ payload }: { payload: LrlcPayload }) {
  const { points, classes } = payload;
  const [axes, setAxes] = useState({ x: DEFAULT_X, y: DEFAULT_Y });
  const [view, setView] = useState<{ x: Window; y: Window }>({ x: WHOLE, y: WHOLE });
  const box = useRef<HTMLDivElement>(null);

  const xChannel = channelOf(axes.x);
  const yChannel = channelOf(axes.y);

  /**
   * Sumbu penuh tiap besaran, dari seluruh sampel yang tergambar.
   *
   * Dari seluruhnya dan bukan dari yang tampak: sumbu yang mengikuti jendela
   * akan berubah ujungnya pada tiap putaran roda, dan zum yang memindahkan
   * datanya sendiri bukan zum melainkan gambar yang lain.
   */
  const homeX = useMemo(
    () => scaleOf(points.channels?.[axes.x] ?? [], xChannel.log),
    [points, axes.x, xChannel.log],
  );
  const homeY = useMemo(
    () => scaleOf(points.channels?.[axes.y] ?? [], yChannel.log),
    [points, axes.y, yChannel.log],
  );

  const scaleX = useMemo(
    () => (homeX ? windowScale(homeX, view.x) : null), [homeX, view.x]);
  const scaleY = useMemo(
    () => (homeY ? windowScale(homeY, view.y) : null), [homeY, view.y]);

  const ticksX = useMemo(() => (scaleX ? ticksOn(scaleX, 6) : []), [scaleX]);
  const ticksY = useMemo(() => (scaleY ? ticksOn(scaleY, 5) : []), [scaleY]);

  /**
   * Titik yang benar-benar tergambar, sudah dalam persen kotaknya.
   *
   * Yang jatuh di luar jendela dibuang di sini dan tidak dijepit ke tepi:
   * sebuah rombongan yang menumpuk di tepi kotak terbaca sebagai rombongan
   * batuan, padahal yang terjadi adalah ia berada di luar gambar.
   */
  const drawn = useMemo(() => {
    const xs = points.channels?.[axes.x] ?? [];
    const ys = points.channels?.[axes.y] ?? [];
    const out: { cx: number; cy: number; fill: string }[] = [];
    if (!scaleX || !scaleY) return out;
    for (let i = 0; i < xs.length; i += 1) {
      const tx = placeOn(scaleX, xs[i] ?? NaN);
      const ty = placeOn(scaleY, ys[i] ?? NaN);
      if (!Number.isFinite(tx) || !Number.isFinite(ty)) continue;
      if (tx < 0 || tx > 1 || ty < 0 || ty > 1) continue;
      out.push({
        cx: PAD + tx * SPAN,
        cy: PAD + (1 - ty) * SPAN,
        fill: colorOf(classes, points.class[i]),
      });
    }
    return out;
  }, [points, axes, scaleX, scaleY, classes]);

  // Rodanya dipasang sendiri, bukan lewat `onWheel`. React memasang `wheel`
  // sebagai pendengar pasif, dan `preventDefault` di dalam sebuah `onWheel`
  // diabaikan: halamannya ikut bergulir sambil gambarnya dizum.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = event.deltaY > 0 ? WHEEL : 1 / WHEEL;
      const tx = within(event.clientX - rect.left, rect.width);
      const ty = 1 - within(event.clientY - rect.top, rect.height);
      setView((current) => ({
        x: zoomWindow(current.x, pointing(current.x, tx), factor),
        y: zoomWindow(current.y, pointing(current.y, ty), factor),
      }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const [panning, setPanning] = useState(false);
  const grab = useRef<{ x: number; y: number } | null>(null);

  /**
   * Benar untuk kejadian yang berasal dari tombol zumnya sendiri.
   *
   * Tombol-tombol itu duduk **di dalam** kotak yang bisa diseret, jadi tanpa
   * penjaga ini sebuah tekanan pada "+" ikut memulai seretan dan merebut
   * penunjuknya — dan klik yang seharusnya menyusul tidak pernah tiba pada
   * tombol yang ditekan orang.
   */
  const onControl = (target: EventTarget | null) =>
    target instanceof Element && target.closest("button") !== null;

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 || onControl(event.target)) return;
    grab.current = { x: event.clientX, y: event.clientY };
    setPanning(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const from = grab.current;
    if (!from) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    grab.current = { x: event.clientX, y: event.clientY };
    // Piksel ke pecahan sumbu penuh: lewat lebar **kotak nilainya**, yang
    // lebih sempit daripada elemennya sebesar bingkainya, lalu dikalikan lebar
    // jendela yang sedang tampak — pada zum sepuluh kali, satu piksel tangan
    // menggeser sepersepuluh jauhnya.
    const wide = (rect.width * SPAN) / 100;
    const tall = (rect.height * SPAN) / 100;
    setView((current) => ({
      x: panWindow(current.x, wide > 0
        ? (-dx / wide) * (current.x.hi - current.x.lo) : 0),
      y: panWindow(current.y, tall > 0
        ? (dy / tall) * (current.y.hi - current.y.lo) : 0),
    }));
  };

  const endPan = (event: React.PointerEvent) => {
    if (!grab.current) return;
    grab.current = null;
    setPanning(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const zoomBy = (factor: number) =>
    setView((current) => ({
      x: zoomWindow(current.x, (current.x.lo + current.x.hi) / 2, factor),
      y: zoomWindow(current.y, (current.y.lo + current.y.hi) / 2, factor),
    }));
  const reset = () => setView({ x: WHOLE, y: WHOLE });

  const home = isWhole(view.x) && isWhole(view.y);
  const options = LRLC_CHANNELS.map((channel) => ({
    value: channel.key,
    label: `${channel.label} — ${channel.name}`,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: "none" }}>
      <div style={{ display: "flex", gap: 9, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Dropdown
          label="Horizontal"
          value={axes.x}
          options={options}
          width={210}
          compact
          onChange={(value) => {
            setAxes((prev) => ({ ...prev, x: value }));
            // Jendela adalah pecahan dari sumbu yang melahirkannya, dan sumbu
            // itu baru saja berganti besaran. Jendela 0,3–0,5 milik RT tidak
            // berarti apa-apa pada RHOB, dan yang menggeser sumbu mendapat
            // kotak yang zumnya dibawa dari gambar yang lain.
            setView((prev) => ({ ...prev, x: WHOLE }));
          }}
        />
        <Dropdown
          label="Vertical"
          value={axes.y}
          options={options}
          width={210}
          compact
          onChange={(value) => {
            setAxes((prev) => ({ ...prev, y: value }));
            setView((prev) => ({ ...prev, y: WHOLE }));
          }}
        />
        <span style={{ flex: 1 }} />
        <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)",
                       paddingBottom: 6 }}>
          wheel zooms · drag pans · double-click fits
        </span>
      </div>

      <div style={{ display: "flex", gap: 6, flex: "none", height: 300 }}>
        <div style={{ display: "flex", flexDirection: "column", width: 52, flex: "none" }}>
          <div style={{ position: "relative", flex: 1 }}>
            {ticksY.map((tick) => (
              <span key={tick.value} style={{
                position: "absolute", right: 4, top: `${PAD + (1 - tick.t) * SPAN}%`,
                transform: "translateY(-50%)",
                font: "400 9px var(--font-mono)", color: "var(--ink-5)",
              }}>
                {tick.text}
              </span>
            ))}
          </div>
          <div style={{ height: 16 }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <div
            ref={box}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            onDoubleClick={(event) => !onControl(event.target) && reset()}
            style={{
              position: "relative", flex: 1,
              border: "1px solid var(--line)", borderRadius: 7,
              background: "var(--panel-inset)", overflow: "hidden",
              cursor: panning ? "grabbing" : "grab", touchAction: "none",
            }}
          >
            <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                 style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
              {ticksX.map((tick) => (
                <line key={`x${tick.value}`}
                      x1={PAD + tick.t * SPAN} y1={PAD}
                      x2={PAD + tick.t * SPAN} y2={PAD + SPAN}
                      stroke="var(--line-soft)" strokeWidth={0.15} />
              ))}
              {ticksY.map((tick) => (
                <line key={`y${tick.value}`}
                      x1={PAD} y1={PAD + (1 - tick.t) * SPAN}
                      x2={PAD + SPAN} y2={PAD + (1 - tick.t) * SPAN}
                      stroke="var(--line-soft)" strokeWidth={0.15} />
              ))}
              {drawn.map((point, index) => (
                <circle key={index} cx={point.cx} cy={point.cy} r={0.45}
                        fill={point.fill} opacity={0.85} />
              ))}
            </svg>

            <span style={{
              position: "absolute", left: 8, top: 6, pointerEvents: "none",
              font: "400 9px var(--font-mono)", color: "var(--ink-6)",
            }}>
              {points.shown.toLocaleString("en-US")} of{" "}
              {points.count.toLocaleString("en-US")} samples drawn
              {home ? "" : ` · ${drawn.length.toLocaleString("en-US")} in view`}
            </span>

            {/* Rodanya sudah cukup bagi yang tahu ia ada, dan itulah masalahnya:
                tak ada apa pun pada sebuah crossplot yang memberi tahu bahwa ia
                bisa diperbesar. */}
            <MapZoomControls
              zoom={Math.max(zoomOf(view.x), zoomOf(view.y))}
              home={home}
              onIn={() => zoomBy(1 / BUTTON)}
              onOut={() => zoomBy(BUTTON)}
              onReset={reset}
              corner="top-right"
            />

            {(!scaleX || !scaleY) && (
              <div style={{ position: "absolute", inset: 0, display: "grid",
                            placeItems: "center", padding: 12 }}>
                <Empty>
                  Nothing to draw against{" "}
                  {!scaleX ? xChannel.label : yChannel.label} — not one drawn
                  sample carries it{(!scaleX ? xChannel : yChannel).log
                    ? " as a positive number" : ""}.
                </Empty>
              </div>
            )}
          </div>

          <div style={{ position: "relative", height: 16, flex: "none" }}>
            {ticksX.map((tick) => (
              <span key={tick.value} style={{
                position: "absolute", left: `${PAD + tick.t * SPAN}%`,
                transform: "translateX(-50%)",
                font: "400 9px var(--font-mono)", color: "var(--ink-5)",
              }}>
                {tick.text}
              </span>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10,
                        alignItems: "center", flex: "none" }}>
            <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-4)" }}>
              {captionOf(xChannel)} — {captionOf(yChannel)}
            </span>
            {occupied(payload.rows).map((row) => (
              <span key={row.code} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2,
                               background: row.color, flex: "none" }} />
                <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
                  {row.label} · {row.thickness.toFixed(1)} m
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
